/**
 * Last-selected-menu persistence — Flutter parity.
 *
 * Flutter (`menu_container_screen.dart`) persists the selected menu's id to
 * `AppPreferenceUtil` under `${ENV}scrips_selected_menu` on every selection,
 * restores it on boot (scripsAdmin role "0" skips restore), and clears it on
 * logout. The fallback when nothing is persisted is the role's default home.
 *
 * React differences (idiomatic, behaviour-faithful):
 *   - We persist the React `NavId` string (e.g. "patients", "billing")
 *     rather than the Flutter dynamic-menu integer id — React's nav set is
 *     static, so the id↔NavId mapping Flutter needs doesn't apply.
 *   - GLOBAL key (no userId), cleared on logout — same scope as Flutter.
 *     localStorage is already origin-scoped, so the ENV prefix is redundant;
 *     the key is plain. (New key, not a migration — the orphan-and-reset
 *     rule for unattributed→per-user keys doesn't apply.)
 *   - Settings (Account / Organization) is NOT a `NavId` and is never
 *     persisted — matches Flutter, which skips persistence for the
 *     id -1 / -2 settings entries.
 *
 * "Do as Flutter does" (Andrew, 2026-06-08). This does NOT override PROD-926's
 * always-Calendar landing: Flutter clears the key on logout and so do we, so a
 * FRESH LOGIN always lands on Calendar. Restore only applies to a session-
 * resume reload (refresh / reopen with a still-valid token). Calendar remains
 * the fallback whenever nothing is persisted.
 */

/**
 * The top-level nav ids. This array is the SINGLE SOURCE OF TRUTH for both
 * the `NavId` union (consumed by App.tsx) and the persistence allow-list —
 * they cannot drift (Samer review on PR #512 §5). Adding a nav here extends
 * the union AND makes it persistable in one edit. Settings (Account /
 * Organization) is intentionally NOT here: it's a route, not a top-level
 * NavId, and Flutter never persists it (id -1 / -2).
 */
export const PERSISTABLE_NAV_IDS = [
  'dashboard',
  'calendar',
  'studio',
  'patients',
  'reports',
  'billing',
  'stock',
] as const;

export type PersistableNavId = (typeof PERSISTABLE_NAV_IDS)[number];

/**
 * The app's top-level nav identifier. Aliased to the persistable set so the
 * allow-list and the union are provably identical — a new NavId is, by
 * construction, also persistable. App.tsx imports this rather than defining
 * its own inline union.
 */
export type NavId = PersistableNavId;

const LAST_NAV_KEY = 'scrips_selected_menu';

/**
 * Query params that make a bare `/` an explicit CALENDAR deep link.
 *
 * `/` is the only route that hosts more than one surface: the calendar
 * renders there directly, while Patients / Dashboard render there via the
 * shell's `stateNav`. The calendar URL-syncs `?date=&view=`
 * (`scheduling-page.tsx:177-187`) so a day is shareable, plus `?actor=devices`
 * for the Devices tab (PROD-2007), so those params — and only those —
 * identify `/` as "the user asked for the calendar".
 *
 * Billing / Reports / Stock / Studio have real pathnames, so a deep link to
 * them is already unambiguous and never reaches this list.
 */
export const ROOT_DEEP_LINK_PARAMS = ['date', 'view', 'actor'] as const;

/**
 * True when `/` carries an explicit calendar deep link (PROD-1776).
 *
 * Precedence decision (PROD-1776, option 1): an explicit URL beats the
 * `lastSelectedMenu` restore. Restore still owns the common bare-`/` case;
 * it just stops silently swallowing a pasted link. Without this, a repro URL
 * only worked when the reader's last-selected menu happened to be Calendar —
 * every React repro link in the 2026-07-28 parity sweep assumed otherwise.
 *
 * An empty value (`?date=`) is not intent, so it does not count.
 */
export function hasRootDeepLink(search: string): boolean {
  try {
    const params = new URLSearchParams(search);
    return ROOT_DEEP_LINK_PARAMS.some((p) => (params.get(p) ?? '') !== '');
  } catch {
    return false;
  }
}

/**
 * Inputs to the restore decision. Kept as a plain object so the precedence
 * rule is unit-testable without mounting the whole app shell.
 */
export interface RestoreNavContext {
  /** `useAuthStore(s => s.status)`. */
  authStatus: string;
  /** Org-admin awaiting the Practice Setup wizard. */
  isWizardRequired: boolean;
  /** Backend numeric-string role (`user.role`), not the JWT `orgRoles` label. */
  role: string | undefined;
  /** `location.pathname`. */
  pathname: string;
  /** `location.search`. */
  search: string;
  /** The scripsAdmin role id, which never restores (Flutter parity). */
  scripsAdminRole: string;
}

/**
 * Whether the `lastSelectedMenu` restore may run (PROD-1776).
 *
 * The whole precedence rule lives here, in order of who wins:
 *   1. not authenticated yet          → no restore (nothing to restore into)
 *   2. onboarding wizard required     → wizard wins; retry after it completes
 *   3. scripsAdmin ("0")              → never restores (Flutter parity)
 *   4. a real pathname (`/billing` …) → that IS the deep link; URL wins
 *   5. `/?date=…&view=…`              → explicit calendar deep link; URL wins
 *   6. bare `/`                       → restore runs (the common case)
 */
export function shouldRestoreLastNav(ctx: RestoreNavContext): boolean {
  if (ctx.authStatus !== 'authenticated') return false;
  if (ctx.isWizardRequired) return false;
  if (ctx.role === ctx.scripsAdminRole) return false;
  if (ctx.pathname !== '/') return false;
  if (hasRootDeepLink(ctx.search)) return false;
  return true;
}

/**
 * Drop the calendar-only params from a `/` query string, preserving every
 * other param and their order.
 *
 * Needed to keep `hasRootDeepLink` honest. The calendar writes `date`/`view`
 * on every render, and they survive an in-app switch to Patients — so after
 * calendar → patients the URL still *looks* like a calendar deep link. Left
 * alone, the next reload would read those leftovers as intent and defeat the
 * restore for the single most common resume path. Stripping them on leave is
 * also what the ticket's option 2 asks for on hygiene grounds: the address
 * bar should not describe a surface that is not on screen.
 *
 * Returns a string with no leading `?` (empty when nothing remains).
 */
export function stripRootDeepLinkParams(search: string): string {
  try {
    const params = new URLSearchParams(search);
    for (const p of ROOT_DEEP_LINK_PARAMS) params.delete(p);
    return params.toString();
  } catch {
    return '';
  }
}

function isPersistable(v: string | null): v is PersistableNavId {
  return v != null && (PERSISTABLE_NAV_IDS as readonly string[]).includes(v);
}

/** Persist the active nav. No-op for unknown ids (e.g. settings). */
export function writeLastNav(id: string): void {
  try {
    if (isPersistable(id)) {
      window.localStorage.setItem(LAST_NAV_KEY, id);
    }
  } catch {
    // localStorage can throw in private-mode / storage-full — never fatal.
  }
}

/** Read the persisted nav, or null when absent/invalid. */
export function readLastNav(): PersistableNavId | null {
  try {
    const v = window.localStorage.getItem(LAST_NAV_KEY);
    return isPersistable(v) ? v : null;
  } catch {
    return null;
  }
}

/** Clear on logout (Flutter clears `lastSelectedMenu` on logout). */
export function clearLastNav(): void {
  try {
    window.localStorage.removeItem(LAST_NAV_KEY);
  } catch {
    // ignore
  }
}
